import type { CSSProperties, ReactNode } from 'react';
import { useLogo } from '../api/hooks';

/**
 * Line-icon set (ported from the prototype's ui-atoms). Stroke icons on a
 * 24×24 viewBox, coloured via `currentColor`. `name` is checked at the type
 * level so a typo is a compile error.
 */
const PATHS = {
  home: <><path d="M3 11l9-7 9 7" /><path d="M5 10v10h14V10" /></>,
  tools: <><path d="M14.7 6.3a4 4 0 00-5.4 5.4l-6 6 2 2 6-6a4 4 0 005.4-5.4l-2.5 2.5-1.5-1.5z" /></>,
  ticket: <><path d="M3 9V6a1 1 0 011-1h16a1 1 0 011 1v3a2 2 0 000 4v3a1 1 0 01-1 1H4a1 1 0 01-1-1v-3a2 2 0 000-4z" /><path d="M9 5v14" /></>,
  chart: <><path d="M3 3v18h18" /><path d="M7 14l3-3 4 4 5-6" /></>,
  bell: <><path d="M6 8a6 6 0 1112 0c0 7 3 8 3 8H3s3-1 3-8z" /><path d="M10 21a2 2 0 004 0" /></>,
  lock: <><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 018 0v4" /></>,
  shield: <><path d="M12 3l8 3v5c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6l8-3z" /></>,
  settings: <><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 00.3 1.8l.1.1a2 2 0 11-2.8 2.8l-.1-.1a1.7 1.7 0 00-1.8-.3 1.7 1.7 0 00-1 1.5V21a2 2 0 11-4 0v-.1a1.7 1.7 0 00-1.1-1.5 1.7 1.7 0 00-1.8.3l-.1.1a2 2 0 11-2.8-2.8l.1-.1a1.7 1.7 0 00.3-1.8 1.7 1.7 0 00-1.5-1H3a2 2 0 110-4h.1a1.7 1.7 0 001.5-1.1 1.7 1.7 0 00-.3-1.8l-.1-.1a2 2 0 112.8-2.8l.1.1a1.7 1.7 0 001.8.3H9a1.7 1.7 0 001-1.5V3a2 2 0 114 0v.1a1.7 1.7 0 001 1.5 1.7 1.7 0 001.8-.3l.1-.1a2 2 0 112.8 2.8l-.1.1a1.7 1.7 0 00-.3 1.8V9a1.7 1.7 0 001.5 1H21a2 2 0 110 4h-.1a1.7 1.7 0 00-1.5 1z" /></>,
  search: <><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /></>,
  plus: <><path d="M12 5v14M5 12h14" /></>,
  chevR: <><path d="M9 6l6 6-6 6" /></>,
  chevL: <><path d="M15 6l-6 6 6 6" /></>,
  chevD: <><path d="M6 9l6 6 6-6" /></>,
  chevU: <><path d="M6 15l6-6 6 6" /></>,
  arrowR: <><path d="M5 12h14M13 6l6 6-6 6" /></>,
  arrowU: <><path d="M12 19V5M6 11l6-6 6 6" /></>,
  arrowD: <><path d="M12 5v14M6 13l6 6 6-6" /></>,
  download: <><path d="M12 3v12M6 9l6 6 6-6" /><path d="M5 21h14" /></>,
  external: <><path d="M14 4h6v6" /><path d="M20 4l-9 9" /><path d="M14 14v6H4V10h6" /></>,
  ext: <><path d="M14 4h6v6" /><path d="M20 4l-9 9" /><path d="M14 14v6H4V10h6" /></>,
  user: <><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0116 0" /></>,
  users: <><circle cx="9" cy="8" r="4" /><circle cx="17" cy="9" r="3" /><path d="M2 20a7 7 0 0114 0" /><path d="M15 20a5 5 0 017-4" /></>,
  eye: <><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7S2 12 2 12z" /><circle cx="12" cy="12" r="3" /></>,
  eyeOff: <><path d="M2 12s3-7 10-7c2 0 4 .5 5.5 1.4" /><path d="M22 12s-3 7-10 7c-2 0-4-.5-5.5-1.4" /><path d="M3 3l18 18" /></>,
  copy: <><rect x="8" y="8" width="12" height="12" rx="2" /><path d="M16 8V5a2 2 0 00-2-2H5a2 2 0 00-2 2v9a2 2 0 002 2h3" /></>,
  calendar: <><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></>,
  filter: <><path d="M3 5h18l-7 9v6l-4-2v-4z" /></>,
  check: <><path d="M5 12l5 5 9-11" /></>,
  x: <><path d="M6 6l12 12M18 6L6 18" /></>,
  edit: <><path d="M4 20h4l11-11-4-4L4 16z" /></>,
  trash: <><path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13" /></>,
  send: <><path d="M22 2L11 13" /><path d="M22 2l-7 20-4-9-9-4z" /></>,
  info: <><circle cx="12" cy="12" r="9" /><path d="M12 8h.01M11 12h1v5h1" /></>,
  warn: <><path d="M12 3l10 18H2L12 3z" /><path d="M12 9v6M12 17h.01" /></>,
  flame: <><path d="M12 2s5 5 5 9a5 5 0 11-10 0c0-2 1-3 1-3s2 4 4 4-2-7 0-10z" /></>,
  menu: <><path d="M4 6h16M4 12h16M4 18h16" /></>,
  refresh: <><path d="M3 12a9 9 0 0115-6.7L21 8" /><path d="M21 3v5h-5" /><path d="M21 12a9 9 0 01-15 6.7L3 16" /><path d="M3 21v-5h5" /></>,
  star: <><path d="M12 3l3 6 7 1-5 5 1 7-6-3-6 3 1-7-5-5 7-1z" /></>,
  sparkle: <><path d="M12 3v4M12 17v4M3 12h4M17 12h4M5.6 5.6l2.8 2.8M15.6 15.6l2.8 2.8M5.6 18.4l2.8-2.8M15.6 8.4l2.8-2.8" /></>,
  link: <><path d="M10 14a5 5 0 007 0l3-3a5 5 0 00-7-7l-1 1" /><path d="M14 10a5 5 0 00-7 0l-3 3a5 5 0 007 7l1-1" /></>,
  folder: <><path d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2z" /></>,
  book: <><path d="M4 4h11a4 4 0 014 4v12H8a4 4 0 01-4-4z" /><path d="M4 16a4 4 0 014-4h11" /></>,
  log: <><path d="M9 5H4v15h16V8h-5" /><path d="M9 3h6v4H9z" /><path d="M8 12h8M8 16h5" /></>,
  bolt: <><path d="M13 2L4 14h7l-1 8 9-12h-7z" /></>,
  list: <><path d="M3 6h18M3 12h18M3 18h18" /></>,
  map: <><path d="M21 10c0 6-9 13-9 13s-9-7-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" /></>,
  mail: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7l9 7 9-7" /></>,
  at: <><circle cx="12" cy="12" r="4" /><path d="M16 8v5a3 3 0 006 0v-1a10 10 0 10-4 8" /></>,
  grip: <><circle cx="9" cy="6" r="1" /><circle cx="9" cy="12" r="1" /><circle cx="9" cy="18" r="1" /><circle cx="15" cy="6" r="1" /><circle cx="15" cy="12" r="1" /><circle cx="15" cy="18" r="1" /></>,
  repeat: <><path d="M17 2l4 4-4 4" /><path d="M3 11v-1a4 4 0 014-4h14" /><path d="M7 22l-4-4 4-4" /><path d="M21 13v1a4 4 0 01-4 4H3" /></>,
  clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
  moon: <><path d="M21 12.8A9 9 0 1111.2 3a7 7 0 009.8 9.8z" /></>,
  sortAZ: <><path d="M3 6h13M3 12h9M3 18h5" /><path d="M17 4v16M14 17l3 3 3-3" /></>,
  tag: <><path d="M20 12l-8 8-9-9V3h8z" /><circle cx="7.5" cy="7.5" r="1.5" /></>,
  cap: <><path d="M22 9L12 5 2 9l10 4 10-4z" /><path d="M6 11v5c0 1 2.7 2.5 6 2.5s6-1.5 6-2.5v-5" /><path d="M22 9v6" /></>,
  play: <><path d="M7 4l13 8-13 8z" /></>,
  image: <><rect x="3" y="4" width="18" height="16" rx="2" /><circle cx="8.5" cy="9.5" r="1.5" /><path d="M21 16l-5-5-6 6-3-3-4 4" /></>,
  award: <><circle cx="12" cy="9" r="6" /><path d="M8.5 14L7 22l5-3 5 3-1.5-8" /></>,
  file: <><path d="M14 3H7a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V8z" /><path d="M14 3v5h5" /></>,
  help: <><circle cx="12" cy="12" r="9" /><path d="M9.5 9.5a2.5 2.5 0 114 2c-.8.6-1.5 1.1-1.5 2.2" /><path d="M12 17.5v.01" /></>,
  video: <><rect x="2" y="6" width="14" height="12" rx="2" /><path d="M16 10l6-3v10l-6-3z" /></>,
  listBullet: <><circle cx="4" cy="7" r="1" /><circle cx="4" cy="12" r="1" /><circle cx="4" cy="17" r="1" /><path d="M9 7h11M9 12h11M9 17h11" /></>,
  listNumber: <><path d="M10 7h10M10 12h10M10 17h10" /><path d="M4 6l1-.5V10" /><path d="M3.5 15.5a1 1 0 111.7.7L3.5 18h2" /></>,
  quote: <><path d="M7 7C5 8 4 10 4 13v4h5v-6H6c0-1.5.6-2.6 2-3.2zM17 7c-2 1-3 3-3 6v4h5v-6h-3c0-1.5.6-2.6 2-3.2z" /></>,
  code: <><path d="M9 8l-5 4 5 4M15 8l5 4-5 4" /></>,
  checklist: <><rect x="3" y="4" width="7" height="7" rx="1.5" /><path d="M5 7.5l1.5 1.5L9 6" /><rect x="3" y="14" width="7" height="6" rx="1.5" /><path d="M13 6h8M13 17h8" /></>,
  hr: <><path d="M4 12h16" /><path d="M6 8h.01M10 8h.01M14 8h.01M18 8h.01M6 16h.01M10 16h.01M14 16h.01M18 16h.01" /></>,
  table: <><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 9h18M3 14h18M9 4v16M15 4v16" /></>,
} satisfies Record<string, ReactNode>;

export type IconName = keyof typeof PATHS;

export function Icon({ name, size = 16, stroke = 1.7 }: { name: IconName; size?: number; stroke?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="nav-item__icon"
    >
      {PATHS[name] ?? null}
    </svg>
  );
}

/** Touristation logo mark — the custom logo (if uploaded) or the default asset. */
export function BrandMark({ size = 32, className = '', style }: { size?: number; className?: string; style?: CSSProperties }) {
  const logo = useLogo();
  const src = logo.data?.value || '/logo.png';
  return (
    <img
      className={className}
      src={src}
      alt="Touristation"
      draggable={false}
      style={{ width: size, height: size, objectFit: 'contain', display: 'block', ...style }}
    />
  );
}

export function Brand({ size = 32 }: { size?: number }) {
  return <BrandMark size={size} className="sidebar__brand-mark" />;
}
